// src/App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { isIOS } from "./utils/platform";
import { ensureWebTokenHydrated } from "./utils/auth-sync";
import { api } from "./api";

import "./App.css";
import "./index.css";

import AppLayout from "./AppLayout"; 
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Home from "./pages/Home";
import ActivityCalendar from "./components/ActivityCalendar";
// import ActivityDetail from "./pages/ActivityDetail";
import ActivityUpload from "./pages/ActivityUpload";
import Circle from "./pages/Circle";
import CircleDiscovery from "./pages/CircleDiscovery";
import CreateCircle from "./pages/CreateCircle";
// import Activities from "./pages/Activities";
import Profile from "./pages/Profile";
import ActivityLightbox from "./components/ActivityLightbox";
import MobileFooterNav from "./components/MobileFooterNav";
import SafeAreaTop from "./SafeAreaTop";
import ActivityPage from "./pages/ActivityPage";
import PlanHub from "./pages/PlanHub";
import MonthlyCalendarPage from "./pages/MonthlyCalendarPage";
import WeeklyCalendarPage from "./pages/WeeklyCalendarPage";
import WeeklyPlanPage from "./pages/WeeklyPlanPage";
import GoalsPage from "./pages/GoalsPage";
import RemindersPage from "./pages/RemindersPage";
import ScrollToTop from "./components/ScrollToTop";
import UploadActivityPage from "pages/UploadActivityPage";
import ConnectionsPage from "pages/ConnectionsPage";
import TagFeed from "./pages/TagFeed";
import GlobalSearchPage from "./pages/GlobalSearchPage";
import CreateCirclePage from "./pages/CreateCirclePage";
import EventCreatePage from "pages/EventCreatePage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import AnnouncementCreatePage from "./pages/AnnouncementCreatePage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import CircleAdminPage from "./pages/CircleAdminPage";
import EventListPage from "./pages/EventListPage";
import EditCircleDetailsPage from "./pages/EditCircleDetailsPage";
import ExplorePreview from "./pages/ExplorePreview";
import ExploreCirclesPreview from "./pages/ExploreCirclesPreview";
import ExploreCircleDetailsPreview from "./pages/ExploreCircleDetailsPreview";
import AboutPage from "./pages/AboutPage";
import ProfileFlowersPage from "./pages/ProfileFlowersPage";
import GothamPotteryPage from "./pages/GothamPotteryPage";
import BklynClayPage from "./pages/BklynClayPage";

import SplashScreen from "./components/SplashScreen";

const isNative =
  typeof Capacitor?.isNativePlatform === "function"
    ? Capacitor.isNativePlatform()
    : !!(
        window?.Capacitor?.isNativePlatform &&
        window.Capacitor.isNativePlatform()
      );
const RouterComponent = isNative ? HashRouter : BrowserRouter;

async function getToken() {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}
  return (
    localStorage.getItem("token") || sessionStorage.getItem("token") || null
  );
}

async function getAuthHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ----------------- helpers for mobile-only chrome ----------------- */
function useIsNarrow(breakpoint = 768) {
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isNarrow;
}

function MobileChrome() {
  const { pathname } = useLocation();
  const onIOS = !!isIOS;

  const [isNarrow, setIsNarrow] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  React.useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isCompact = onIOS || isNarrow;

  const hideMobileFooter = [
    "/",
    "/login",
  ].includes(pathname);

  const isPreviewPage = [
    "/explore",
    "/circles-preview",
    "/circle-details-preview",
    "/gothampotterydemo",
    "/bklynclaydemo",
    "/about",
  ].includes(pathname);

  const isAuthFlowPage = [
    "/signup",
    "/forgotpassword",
    "/resetpassword",
  ].includes(pathname);

  if (!isCompact || hideMobileFooter) return null;

  const FOOTER_H_PX = 56;

  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none"
        style={{
          height: `calc(env(safe-area-inset-bottom) + ${FOOTER_H_PX}px)`,
        }}
      />

      <MobileFooterNav
        redirectAllTo={
          isPreviewPage
            ? "/signup"
            : isAuthFlowPage
              ? "/login"
              : null
        }
      />
    </>
  );
}

// ---------- Background Wrapper ----------
function PageWrapper({ children }) {
  const location = useLocation();

  const isAuthPage = ["/", "/login", "/signup", "/forgotpassword", "/resetpassword"].some(
    (p) => location.pathname === p
  );

  // switch background
  const bgClass = isAuthPage ? "bg-blue-50" : "bg-white";

  return (
    <div className={`${bgClass} min-h-[100svh] text-gray-900 transition-colors duration-200`}>
      {children}
    </div>
  );
}
// ----------------------------------------

function LoginOrHome() {
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        // Important for Capacitor/native → web token synchronization.
        await ensureWebTokenHydrated();

        const token = await getToken();

        if (!token) {
          if (!cancelled) {
            setIsAuthenticated(false);
            setCheckingAuth(false);
          }
          return;
        }

        await api.get("/users/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });

        if (!cancelled) {
          setIsAuthenticated(true);
        }
      } catch {
        // The token is missing, invalid, or expired.
        if (!cancelled) {
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) {
          setCheckingAuth(false);
        }
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  if (checkingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-blue-50">
        <p className="text-sm font-medium text-[#6f665d]">Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? (
    <Navigate to="/home" replace />
  ) : (
    <Login />
  );
}

export default function App() {
  const [lbOpen, setLbOpen] = useState(false);
  const [lbId, setLbId] = useState(null);
  const [lbActivity, setLbActivity] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Load current user once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await api.get("/users/me", {
          headers,
          withCredentials: true,
        });
        if (!cancelled) setCurrentUser(res?.data || null);
      } catch {
        if (!cancelled) setCurrentUser(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    ensureWebTokenHydrated();
  }, []);

  // Global event: open activity lightbox
  useEffect(() => {
    let disposed = false;
    const handler = async (e) => {
      const id = e?.detail?.id;
      if (!id) return;
      let activity = e?.detail?.activity || null;
      if (!activity) {
        try {
          const headers = await getAuthHeaders();
          const res = await api.get(`/activities/${id}`, {
            headers,
            withCredentials: true,
          });
          activity = res?.data || null;
        } catch {
          activity = null;
        }
      }
      if (!disposed) {
        setLbId(id);
        setLbActivity(activity);
        setLbOpen(true);
      }
    };
    window.addEventListener("activity:open", handler);
    return () => {
      disposed = true;
      window.removeEventListener("activity:open", handler);
    };
  }, []);

  return (
    <RouterComponent>
      <PageWrapper>
        <ScrollToTop />
        <SafeAreaTop />
        <Routes element={<AppLayout />}>
          <Route path="/" element={<LoginOrHome />} />
          <Route path="/login" element={<LoginOrHome />} />
          <Route path="/explore" element={<ExplorePreview />} />
          <Route path="/circles-preview" element={<ExploreCirclesPreview />} />
          <Route path="/circle-details-preview" element={<ExploreCircleDetailsPreview />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/gothampotterydemo" element={<GothamPotteryPage />} />
          <Route path="/bklynclaydemo" element={<BklynClayPage />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/home" element={<Home />} />
          <Route path="/upload" element={<ActivityUpload />} />
          <Route path="/circles/:id" element={<Circle />} />
          <Route path="/circles/:id/admin" element={<CircleAdminPage />} />
          <Route path="/groups" element={<CircleDiscovery />} />
          <Route path="/circles/new" element={<CreateCirclePage />} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="/profile/:id/shoutouts" element={<ProfileFlowersPage />} />
          <Route path="/activity/:id" element={<ActivityPage />} />
          <Route path="/plan" element={<PlanHub />} />
          <Route path="/plan/monthly" element={<MonthlyCalendarPage />} />
          <Route path="/plan/weekly" element={<WeeklyCalendarPage />} />
          <Route path="/plan/weekly-plan" element={<WeeklyPlanPage />} />
          <Route path="/plan/goals" element={<GoalsPage />} />
          <Route path="/plan/reminders" element={<RemindersPage />} />
          <Route path="/uploadactivity" element={<UploadActivityPage />} />
          <Route path="/connections" element={<ConnectionsPage />} />
          <Route path="/search" element={<GlobalSearchPage />} />
          <Route path="/tags/:tag" element={<TagFeed />} />
          <Route path="/circles/:id/announcements" element={<AnnouncementsPage />} />
          <Route path="/circles/:id/announcements/new" element={<AnnouncementCreatePage />} />
          <Route path="/circles/:id/events/new" element={<EventCreatePage />} />
          <Route path="/circles/:id/events/list" element={<EventListPage />} />
          <Route path="/circles/:id/edit-details" element={<EditCircleDetailsPage />} />
          <Route path="/settings" element={<AccountSettingsPage />} />
          <Route
            path="*"
            element={<Navigate to={isNative ? "/home" : "/"} replace />}
          />
        </Routes>

        <ActivityLightbox
          open={lbOpen}
          activityId={lbId}
          activity={lbActivity}
          currentUser={currentUser}
          onClose={() => {
            setLbOpen(false);
            setLbId(null);
            setLbActivity(null);
          }}
        />

        {/* Mobile-only footer dock (iOS + small screens, not on auth pages) */}
        <MobileChrome />
      </PageWrapper>
    </RouterComponent>
  );
}
